// Browser-side Cloudflare Calls (RTC) client. The worker proxies the REST API
// so neither students nor the operator electron renderer ever see the App
// secret directly.

export interface RemoteTrackEntry {
  tileId: number;
  callsSessionId: string;
  tracks: { trackName: string; kind?: 'video' | 'audio' }[];
  url?: string;
  registeredAt: number;
}

export interface CallsClientConfig {
  appId: string | null;
  apiBase: string;
  operatorBearer?: string;
}

export interface SubscribeOptions {
  /** Called when the underlying RTCPeerConnection enters failed/disconnected
   * for longer than the debounce window AFTER the stream had already been
   * resolved. Caller is expected to teardown + re-subscribe with the latest
   * known entry for the same tileId. */
  onLost?: () => void;
}

export interface CallsClient {
  subscribe(entry: RemoteTrackEntry, opts?: SubscribeOptions): Promise<MediaStream>;
  destroy(): void;
}

interface SubscriberContext {
  pc: RTCPeerConnection;
  sessionId: string | null;
  remoteStream: MediaStream;
  resolved: boolean;
  resolve: (s: MediaStream) => void;
  reject: (e: Error) => void;
  lostTimer?: number;
  lostFired: boolean;
  statsTimer?: number;
}

// Cloudflare Calls expects a single REST round-trip per offer/answer; the
// SDP must contain all ICE candidates up-front. Wait for gathering complete
// before reading pc.localDescription.
function waitForIceGathering(pc: RTCPeerConnection, timeoutMs = 4000): Promise<void> {
  if (pc.iceGatheringState === 'complete') return Promise.resolve();
  return new Promise<void>((resolve) => {
    let done = false;
    const finish = () => { if (done) return; done = true; resolve(); };
    const t = window.setTimeout(finish, timeoutMs);
    pc.addEventListener('icegatheringstatechange', () => {
      if (pc.iceGatheringState === 'complete') { window.clearTimeout(t); finish(); }
    });
  });
}

export function newCallsClient(cfg: CallsClientConfig): CallsClient {
  const subscribers: SubscriberContext[] = [];

  const headers = (): Record<string, string> => {
    const h: Record<string, string> = { 'Content-Type': 'application/json' };
    if (cfg.operatorBearer) h['Authorization'] = `Bearer ${cfg.operatorBearer}`;
    return h;
  };

  async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
    const r = await fetch(`${cfg.apiBase}${path}`, {
      ...init,
      headers: { ...headers(), ...(init.headers || {}) },
    });
    if (!r.ok) {
      const text = await r.text().catch(() => '');
      throw new Error(`${path} -> ${r.status} ${text}`);
    }
    return r.json() as Promise<T>;
  }

  async function subscribe(entry: RemoteTrackEntry, opts: SubscribeOptions = {}): Promise<MediaStream> {
    if (!cfg.appId) throw new Error('CLOUDFLARE_CALLS_APP_ID not configured');

    const pc = new RTCPeerConnection({
      iceServers: [{ urls: 'stun:stun.cloudflare.com:3478' }],
      bundlePolicy: 'max-bundle',
    });
    const remoteStream = new MediaStream();

    const ctx: SubscriberContext = {
      pc, sessionId: null, remoteStream,
      resolved: false,
      resolve: () => {}, reject: () => {},
      lostFired: false,
    };
    subscribers.push(ctx);

    const ready = new Promise<MediaStream>((resolve, reject) => {
      ctx.resolve = resolve; ctx.reject = reject;
    });

    pc.addEventListener('track', (ev) => {
      ev.streams[0]?.getTracks().forEach((t) => remoteStream.addTrack(t));
      if (!ctx.resolved) {
        ctx.resolved = true;
        ctx.resolve(remoteStream);
        startMediaFlowWatchdog();
      }
    });
    pc.addEventListener('connectionstatechange', () => {
      const s = pc.connectionState;
      if (!ctx.resolved) {
        if (['failed', 'closed', 'disconnected'].includes(s)) {
          ctx.reject(new Error('pc ' + s));
        }
        return;
      }
      // Already resolved — monitor for post-resolve drops.
      if (s === 'failed' || s === 'disconnected') {
        if (ctx.lostTimer == null) {
          ctx.lostTimer = window.setTimeout(() => {
            ctx.lostTimer = undefined;
            if (ctx.lostFired) return;
            // Re-check at fire time: a transient blip may have recovered.
            if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected') {
              ctx.lostFired = true;
              try { opts.onLost && opts.onLost(); } catch {}
            }
          }, 5000);
        }
      } else {
        if (ctx.lostTimer != null) { window.clearTimeout(ctx.lostTimer); ctx.lostTimer = undefined; }
      }
    });

    // Media-flow watchdog: pc.connectionState can stay 'connected' even
    // when no RTP arrives (Cloudflare drops a publisher silently, the
    // operator's screencast pauses across navigation, etc). Poll
    // getStats and fire onLost if inbound video bytesReceived is flat
    // for 4s. Caller's onLost handler tears down + re-subscribes.
    let mfLastBytes = -1;
    let mfStagnantSince = 0;
    function startMediaFlowWatchdog() {
      const interval = window.setInterval(async () => {
        if (ctx.lostFired) { window.clearInterval(interval); return; }
        if (pc.connectionState === 'closed' || pc.connectionState === 'failed') {
          window.clearInterval(interval); return;
        }
        try {
          const stats = await pc.getStats();
          let inboundBytes = 0;
          stats.forEach((s: any) => {
            if (s.type === 'inbound-rtp' && s.kind === 'video') {
              inboundBytes += (s.bytesReceived || 0) as number;
            }
          });
          if (mfLastBytes < 0) { mfLastBytes = inboundBytes; return; }
          if (inboundBytes > mfLastBytes) {
            mfLastBytes = inboundBytes;
            mfStagnantSince = 0;
          } else {
            if (!mfStagnantSince) mfStagnantSince = Date.now();
            else if (Date.now() - mfStagnantSince > 4000) {
              console.warn('[calls] inbound video stagnant 4s, firing onLost');
              ctx.lostFired = true;
              window.clearInterval(interval);
              try { opts.onLost && opts.onLost(); } catch {}
            }
          }
        } catch { /* ignore transient getStats errors */ }
      }, 2000);
      ctx.statsTimer = interval;
    }

    for (const t of entry.tracks) {
      pc.addTransceiver(t.kind || 'video', { direction: 'recvonly' });
    }

    const sess = await api<{ ok: true; sessionId: string }>(`/session`, { method: 'POST' });
    ctx.sessionId = sess.sessionId;

    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    await waitForIceGathering(pc);
    const finalOffer = pc.localDescription!;

    const tracksReq = {
      sessionDescription: { sdp: finalOffer.sdp, type: 'offer' },
      tracks: entry.tracks.map((t) => ({
        location: 'remote',
        trackName: t.trackName,
        sessionId: entry.callsSessionId,
      })),
    };
    const tracksRes = await api<{
      ok: true;
      sessionDescription?: { type: string; sdp: string };
      requiresImmediateRenegotiation?: boolean;
    }>(`/session/${sess.sessionId}/tracks/new`, {
      method: 'POST',
      body: JSON.stringify(tracksReq),
    });

    if (tracksRes.sessionDescription) {
      await pc.setRemoteDescription({
        type: tracksRes.sessionDescription.type as RTCSdpType,
        sdp: tracksRes.sessionDescription.sdp,
      });
    }

    return ready;
  }

  function destroy() {
    for (const s of subscribers) {
      if (s.statsTimer != null) { try { window.clearInterval(s.statsTimer); } catch {} }
      if (s.lostTimer != null) { try { window.clearTimeout(s.lostTimer); } catch {} }
      try { s.pc.close(); } catch {}
    }
    subscribers.length = 0;
  }

  return { subscribe, destroy };
}
